import Pusher from 'pusher-js';
import notificationService from './notificationService';
import api from '../utils/api';
import pusherConfigService from './configService';

class PusherService {
  constructor() {
    this.pusher = null;
    this.channels = {};
    this.config = null;
    this.isConnected = false;
    this.userId = null;
    this.additionalUserIds = []; // Store additional user IDs to subscribe to
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000; // 5 seconds
  }

  // Initialize Pusher with configuration
  async initialize(userId, additionalUserIds = []) {
    try {
      // IMPORTANT: Check if user is authenticated before initializing
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const hasToken = !!localStorage.getItem('token');

      if (!isAuthenticated || !hasToken) {
        return false;
      }

      // Always disconnect existing instance before reinitializing
      if (this.pusher) {
        this.disconnect();
      }

      let pusherConfig = null;

      try {
        // Call getPusherConfig() to fetch from backend
        pusherConfig = await pusherConfigService.getPusherConfig();

        // Save to localStorage for future reference
        localStorage.setItem('pusher_config', JSON.stringify(pusherConfig));
      } catch (configError) {
        const savedConfig = localStorage.getItem('pusher_config');
        if (savedConfig) {
          try {
            pusherConfig = JSON.parse(savedConfig);
          } catch (e) {
            console.error('❌ Failed to parse localStorage config:', e);
          }
        }
      }

      if (!pusherConfig) {
        return false;
      }

      // Use fetched config
      this.config = {
        key: pusherConfig.PUSHER_APP_KEY,
        cluster: pusherConfig.PUSHER_APP_CLUSTER || 'mt1',
        encrypted: pusherConfig.PUSHER_APP_ENCRYPTED === true || pusherConfig.PUSHER_APP_ENCRYPTED === '1' || true,
        useTLS: pusherConfig.PUSHER_USE_TLS === true || pusherConfig.PUSHER_USE_TLS === '1' || true
      };

      if (!this.config.key || this.config.key === 'your-pusher-key' || !this.config.cluster) {
        
        return false;
      }

      // Store user ID for channel subscriptions
      this.userId = userId;
      this.additionalUserIds = additionalUserIds;

      // Get fresh auth token for private channels
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL;

      if (!token) {
        return false;
      }

      // Disable Pusher logging
      Pusher.logToConsole = false;

      // Initialize Pusher client with proper configuration
      // Use getter for Authorization header to always get fresh token
      this.pusher = new Pusher(this.config.key, {
        cluster: this.config.cluster,
        forceTLS: this.config.useTLS === true || this.config.useTLS === '1',
        encrypted: this.config.encrypted === true || this.config.encrypted === '1',
        enabledTransports: ['ws', 'wss'],
        disabledTransports: [],
        disableStats: true, // Disable stats to reduce console noise
        authEndpoint: `${apiUrl}/broadcasting/auth`,
        auth: {
          headers: {
            // Always get fresh token from localStorage on every auth request
            get Authorization() {
              const currentToken = localStorage.getItem('token');
              return currentToken ? `Bearer ${currentToken}` : '';
            },
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      });

      // Bind connection events
      this.setupConnectionHandlers();

      // Wait for connection before subscribing to channels
      await this.waitForConnection();

      // Subscribe to default channels AFTER connection is established
      this.subscribeToDefaultChannels();

      // Subscribe to additional user channels if provided
      if (additionalUserIds.length > 0) {
        this.subscribeToAdditionalUsers(additionalUserIds);
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  // Setup connection event handlers
  setupConnectionHandlers() {
    if (!this.pusher) return;

    this.pusher.connection.bind('connected', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
    });

    this.pusher.connection.bind('disconnected', () => {
      this.isConnected = false;
      this.handleReconnect();
    });

    this.pusher.connection.bind('error', (error) => {
      this.isConnected = false;
      this.handleReconnect();
    });

    // Handle connection state changes
    this.pusher.connection.bind('state_change', (states) => {

      if (states.current === 'connecting') {
      } else if (states.current === 'unavailable') {
      } else if (states.current === 'failed') {
      }
    });
  }

  // Wait for Pusher connection to be established
  async waitForConnection(timeout = 10000) {
    if (!this.pusher) {
      return false;
    }

    const currentState = this.pusher.connection.state;

    // If already connected, return immediately
    if (currentState === 'connected') {
      this.isConnected = true;
      return true;
    }

    // Wait for connection
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pusher.connection.unbind('connected', onConnected);
        this.pusher.connection.unbind('failed', onFailed);
        this.pusher.connection.unbind('unavailable', onUnavailable);
        resolve(false);
      }, timeout);

      const onConnected = () => {
        clearTimeout(timer);
        this.pusher.connection.unbind('connected', onConnected);
        this.pusher.connection.unbind('failed', onFailed);
        this.pusher.connection.unbind('unavailable', onUnavailable);
        this.isConnected = true;
        resolve(true);
      };

      const onFailed = () => {
        clearTimeout(timer);
        this.pusher.connection.unbind('connected', onConnected);
        this.pusher.connection.unbind('failed', onFailed);
        this.pusher.connection.unbind('unavailable', onUnavailable);
        resolve(false);
      };

      const onUnavailable = () => {
        clearTimeout(timer);
        this.pusher.connection.unbind('connected', onConnected);
        this.pusher.connection.unbind('failed', onFailed);
        this.pusher.connection.unbind('unavailable', onUnavailable);
        resolve(false);
      };

      this.pusher.connection.bind('connected', onConnected);
      this.pusher.connection.bind('failed', onFailed);
      this.pusher.connection.bind('unavailable', onUnavailable);
    });
  }

  // Handle reconnection logic
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      if (!this.isConnected && this.pusher) {
        this.pusher.connect();
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  // Subscribe to default channels
  subscribeToDefaultChannels() {
    if (!this.pusher || !this.userId) return;


    // Subscribe to global notifications channel
    this.subscribeToChannel('global-notifications', {
      'notification': this.handleGlobalNotification.bind(this),
      'announcement': this.handleAnnouncement.bind(this),
      'test-notification': this.handleTestNotification.bind(this)
    });

    // Subscribe to user-specific private channel
    // Note: subscribeToChannel() will automatically add 'private-' prefix for App.User.* channels
    const userChannel = `App.User.${this.userId}`;

    this.subscribeToChannel(userChannel, {
      'TestPROD': this.handleTestPROD.bind(this),
      'notification': this.handleUserNotification.bind(this),
      'test-notification': this.handleTestNotification.bind(this),
      'dataroom-invite': this.handleDataroomInvite.bind(this),
      'subscription-update': this.handleSubscriptionUpdate.bind(this),
      'file-shared': this.handleFileShared.bind(this)
    });
    
    // Subscribe to test notifications channel
    this.subscribeToChannel('test-notifications', {
      'test': this.handleTestNotification.bind(this),
      'notification': this.handleTestNotification.bind(this)
    });
  }

  // Subscribe to additional user channels for multi-user notifications
  subscribeToAdditionalUsers(userIds) {
    if (!this.pusher || !Array.isArray(userIds)) return;

    userIds.forEach(userId => {
      // Subscribe to each additional user's private channel
      // Note: subscribeToChannel() will automatically add 'private-' prefix for App.User.* channels
      const userChannel = `App.User.${userId}`;

      this.subscribeToChannel(userChannel, {
        'TestPROD': this.handleTestPROD.bind(this),
        'notification': this.handleUserNotification.bind(this),
        'test-notification': this.handleTestNotification.bind(this),
        'dataroom-invite': this.handleDataroomInvite.bind(this),
        'subscription-update': this.handleSubscriptionUpdate.bind(this),
        'file-shared': this.handleFileShared.bind(this)
      });
    });
  }

  // Subscribe to multiple user emails
  async subscribeToUsersByEmail(emails) {
    if (!Array.isArray(emails)) return;

    try {
      // Get user IDs from emails via API
      const response = await api.post('/users/get-ids-by-emails', { emails });
      const userIds = response.data?.userIds || [];
      
      if (userIds.length > 0) {
        this.subscribeToAdditionalUsers(userIds);
        this.additionalUserIds = [...this.additionalUserIds, ...userIds];
      }
      
      return userIds;
    } catch (error) {
      return [];
    }
  }

  // Subscribe to a channel with event handlers
  subscribeToChannel(channelName, eventHandlers = {}) {
    if (!this.pusher) {
      return null;
    }

    // IMPORTANT: For raw Pusher.js (not Laravel Echo), private channels MUST be prefixed with "private-"
    // Detect if this is a private channel that needs authentication
    const isPrivateChannel = channelName.startsWith('App.User.') || channelName.startsWith('presence-');
    const fullChannelName = isPrivateChannel && !channelName.startsWith('private-')
      ? `private-${channelName}`
      : channelName;

    if (isPrivateChannel) {
    }

    // Check if already subscribed - use full channel name for lookup
    if (this.channels[fullChannelName]) {
      // Channel already exists, but we might need to add new event handlers
      const channel = this.channels[fullChannelName];

      // Unbind existing handlers and rebind to avoid duplicates
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        // Unbind any existing handler for this event
        channel.unbind(event);
        // Bind the new handler
        channel.bind(event, handler);
      });

      return channel;
    }

    // Subscribe to channel with proper name (prefixed for private channels)
    const channel = this.pusher.subscribe(fullChannelName);

    // Log subscription success/failure with detailed info
    channel.bind('pusher:subscription_succeeded', () => {
    });

    channel.bind('pusher:subscription_error', (error) => {
    });

    // Bind event handlers
    Object.entries(eventHandlers).forEach(([event, handler]) => {
      channel.bind(event, handler);
    });

    // Store channel reference with full channel name
    this.channels[fullChannelName] = channel;

    return channel;
  }

  // Unsubscribe from a channel
  unsubscribeFromChannel(channelName) {
    if (!this.pusher || !this.channels[channelName]) return;

    this.pusher.unsubscribe(channelName);
    delete this.channels[channelName];
  }

  // Send notification from client side (for testing or admin features)
  async sendNotification(data) {
    try {
      // Support multiple recipients
      let recipients = data.recipients || [];

      // If recipients is an array of emails or user IDs, keep as is
      // If it's a string and contains comma-separated values, split it
      if (typeof recipients === 'string') {
        if (recipients.includes(',')) {
          recipients = recipients.split(',').map(r => r.trim()).filter(r => r);
        } else if (recipients !== 'all' && recipients !== '') {
          recipients = [recipients];
        } else {
          recipients = [];
        }
      }

      // Get current user ID
      const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
      const userId = data.user_id || currentUser?.id || null;

      // Build payload
      const payload = {
        title: data.title,
        body: data.body || data.message,
        message: data.message || data.body, // Include both for compatibility
        type: data.type || 'info'
      };

      // Handle multiple recipients properly
      if (recipients && recipients.length > 0) {
        // Send to multiple recipients
        payload.recipient_emails = recipients;

        // Subscribe to additional user channels if they're user IDs
        const userIdPattern = /^\d+$/;
        const userIds = recipients.filter(r => userIdPattern.test(r));
        if (userIds.length > 0) {
          this.subscribeToAdditionalUsers(userIds);
        }
      } else if (userId) {
        // Send to single user
        payload.user_id = userId;
      }

      // Add optional fields if present
      if (data.action_url || data.url) {
        payload.action_url = data.action_url || data.url;
      }

      // Include additional data for routing
      if (data.channel) {
        payload.channel = data.channel;
      }

      if (data.event) {
        payload.event = data.event;
      }
      // Use test-notification endpoint for all notification sending
      const response = await api.post('/test-notification', payload);

      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Send notification to multiple users by email
  async sendNotificationToEmails(emails, notificationData) {
    // Handle empty emails array - send to current user
    if (!emails || emails.length === 0) {
      // Get current user from localStorage
      const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
      
      if (currentUser?.id) {
        // Send using user_id for current user
        return this.sendNotification({
          ...notificationData,
          user_id: currentUser.id
        });
      } else {
        throw new Error('No current user found');
      }
    }
    
    if (!Array.isArray(emails)) {
      emails = [emails];
    }

    // Check if sending to current user by their ID (not email)
    const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
    if (emails.length === 1 && emails[0] === currentUser?.id) {
      // Sending to current user by ID
      return this.sendNotification({
        ...notificationData,
        user_id: currentUser.id
      });
    }

    return this.sendNotification({
      ...notificationData,
      recipients: emails,
      recipientType: 'email'
    });
  }

  // Send notification to multiple users by ID
  async sendNotificationToUserIds(userIds, notificationData) {
    if (!Array.isArray(userIds)) {
      userIds = [userIds];
    }

    return this.sendNotification({
      ...notificationData,
      recipients: userIds,
      recipientType: 'userId'
    });
  }

  // Event Handlers
  async handleTestPROD(data) {

    // Check and show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          data.type || 'info',
          data.title || 'Test Notification',
          data.body || data.message || 'TestPROD notification received',
          {
            onClick: () => {
              if (data.url || data.action_url) {
                window.location.href = data.url || data.action_url;
              }
            }
          }
        );
      } catch (error) {
        console.error('Failed to show TestPROD notification:', error);
      }
    }

    // Trigger custom event for app to handle
    window.dispatchEvent(new CustomEvent('pusher:testprod', {
      detail: data
    }));
  }

  async handleGlobalNotification(data) {
    
    // Check and show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          data.type || 'info',
          data.title || 'Notification',
          data.body || data.message || '',
          {
            onClick: () => {
              if (data.url) {
                window.location.href = data.url;
              }
            }
          }
        );
      } catch (error) {
      }
    } else {
    }

    // Trigger custom event for app to handle
    window.dispatchEvent(new CustomEvent('pusher:notification', { 
      detail: data 
    }));
  }

  async handleAnnouncement(data) {
    
    // Show browser notification for important announcements
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          'info',
          '📢 ' + (data.title || 'Announcement'),
          data.body || data.message || '',
          {
            requireInteraction: true,
            onClick: () => {
              if (data.url) {
                window.location.href = data.url;
              }
            }
          }
        );
      } catch (error) {
      }
    }

    // Trigger custom event
    window.dispatchEvent(new CustomEvent('pusher:announcement', { 
      detail: data 
    }));
  }

  async handleUserNotification(data) {
    
    // Check and show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notificationResult = await notificationService.showTypedNotification(
          data.type || 'info',
          data.title || 'Personal Notification',
          data.body || data.message || '',
          {
            onClick: () => {
              if (data.url) {
                window.location.href = data.url;
              }
            }
          }
        );
        
      } catch (error) {
      }
    } else {
    }

    // Trigger custom event
    window.dispatchEvent(new CustomEvent('pusher:user-notification', { 
      detail: data 
    }));
  }

  async handleDataroomInvite(data) {
    
    // Show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          'info',
          '📁 New Dataroom Invitation',
          `You've been invited to ${data.dataroom_name || 'a dataroom'}`,
          {
            onClick: () => {
              window.location.href = '/my-invitations';
            }
          }
        );
      } catch (error) {
      }
    }

    // Trigger custom event
    window.dispatchEvent(new CustomEvent('pusher:dataroom-invite', { 
      detail: data 
    }));
  }

  async handleSubscriptionUpdate(data) {
    
    // Show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          data.type || 'info',
          '💳 Subscription Update',
          data.body || data.message || 'Your subscription has been updated',
          {
            onClick: () => {
              window.location.href = '/subscriptions';
            }
          }
        );
      } catch (error) {
      }
    }

    // Trigger custom event
    window.dispatchEvent(new CustomEvent('pusher:subscription-update', { 
      detail: data 
    }));
  }

  async handleFileShared(data) {
    
    // Show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await notificationService.showTypedNotification(
          'info',
          '📄 File Shared',
          `${data.sender_name || 'Someone'} shared "${data.file_name || 'a file'}" with you`,
          {
            onClick: () => {
              if (data.dataroom_id) {
                window.location.href = `/datarooms/${data.dataroom_id}/files`;
              }
            }
          }
        );
      } catch (error) {
      }
    }

    // Trigger custom event
    window.dispatchEvent(new CustomEvent('pusher:file-shared', { 
      detail: data 
    }));
  }
  
  async handleTestNotification(data) {
    
    // Check if notifications are supported and permission is granted
    if (!('Notification' in window)) {
      return;
    }
    
    if (Notification.permission !== 'granted') {
      // Still trigger the custom event so the UI can show something
    } else {
      // Show browser notification
      try {
        const notificationResult = await notificationService.showTypedNotification(
          data.type || 'info',
          '🔔 ' + (data.title || 'Test Notification'),
          data.body || data.message || 'This is a test notification',
          {
            onClick: () => {
              if (data.url || data.action_url) {
                window.location.href = data.url || data.action_url;
              }
            }
          }
        );
        
      } catch (error) {
      }
    }

    // Trigger custom event for the UI to handle
    window.dispatchEvent(new CustomEvent('pusher:notification', { 
      detail: {
        ...data,
        isTest: true
      }
    }));
  }

  // Trigger a client event (for private/presence channels)
  triggerClientEvent(channelName, eventName, data) {
    if (!this.channels[channelName]) {
      return false;
    }

    try {
      this.channels[channelName].trigger(`client-${eventName}`, data);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get connection state
  getConnectionState() {
    if (!this.pusher) return 'disconnected';
    return this.pusher.connection.state;
  }

  // Unsubscribe from user-specific channel (for logout)
  unsubscribeUserChannel() {
    if (!this.userId) return;

    const userChannel = `App.User.${this.userId}`;
    this.unsubscribeFromChannel(userChannel);

    // Also unsubscribe from additional user channels
    this.additionalUserIds.forEach(userId => {
      const channel = `App.User.${userId}`;
      this.unsubscribeFromChannel(channel);
    });

    // Clear additional user IDs
    this.additionalUserIds = [];
  }

  // Disconnect from Pusher
  disconnect() {
    if (!this.pusher) return;

    // Unsubscribe from all channels
    Object.keys(this.channels).forEach(channelName => {
      this.unsubscribeFromChannel(channelName);
    });

    // Disconnect Pusher instance
    this.pusher.disconnect();

    // Reset all state
    this.pusher = null;
    this.isConnected = false;
    this.channels = {};
    this.userId = null;
    this.additionalUserIds = [];
    this.reconnectAttempts = 0;

  }
}

// Create singleton instance
const pusherService = new PusherService();

export default pusherService;